import { cloneDeep } from "lodash";
import {
  websiteBuilderGenerateDefaultButton,
  IWebsiteBuilderButton,
  isWebsiteBuilderButton,
} from "../element/websiteBuilderButton";
import { websiteBuilderGenerateDefaultLogo, IWebsiteBuilderLogo, isWebsiteBuilderLogo } from "../element/websiteBuilderLogo";
import {
  websiteBuilderGenerateDefaultNavigationMenu,
  IWebsiteBuilderNavigationMenu,
  isWebsiteBuilderNavigationMenu,
} from "../element/websiteBuilderNavigationMenu";
import { isWebsiteBuilderElement, TWebsiteBuilderElement } from "../element/websiteBuilderElement";
import { isWebsiteBuilderParentNode, TWebsiteBuilderNode } from "../websiteBuilderNode";
import { IWebsiteBuilderHeader } from "./websiteBuilderHeader";
import { TWebsiteBuilderHideableNodeVisibility } from "../websiteBuilderHideableNode";
import {
  TWebsiteBuilderDevice,
  WEBSITE_BUILDER_DESIGN_SPACE_BOUNDARY_DESKTOP_WIDTH,
  WEBSITE_BUILDER_DESIGN_SPACE_BOUNDARY_MOBILE_WIDTH,
  WEBSITE_BUILDER_DESIGN_SPACE_BOUNDARY_TABLET_WIDTH,
} from "../../website/shared/device";

export const WEBSITE_BUILDER_HEADER_PRESETS = [
  "logo-left-links-right",
  "logo-left-links-right-cta-right",
  "logo-left-links-center-cta-right",
  "logo-center-links-left-cta-right",
  "logo-left-links-left-cta-right",
] as const;

export type TWebsiteBuilderHeaderPreset = typeof WEBSITE_BUILDER_HEADER_PRESETS[number];

type TDeviceValues = Record<TWebsiteBuilderDevice, number>;

const DEVICES: TWebsiteBuilderDevice[] = ["desktop", "tablet", "mobile"];

const HEADER_LAYOUT_GAP = 24;
const HEADER_MAIN_ROW_HEIGHT: TDeviceValues = { desktop: 92, tablet: 92, mobile: 100 };
const HEADER_CONTENT_INSET: TDeviceValues = { desktop: 0, tablet: 32, mobile: 25 };
const HEADER_LOGO_WIDTH: TDeviceValues = { desktop: 92, tablet: 92, mobile: 75 };
const HEADER_LOGO_HEIGHT: TDeviceValues = { desktop: 92, tablet: 92, mobile: 75 };
const HEADER_DESKTOP_MENU_WIDTH = 300;
const HEADER_DESKTOP_MENU_HEIGHT = 50;
const HEADER_COMPACT_MENU_WIDTH = 25;
const HEADER_COMPACT_MENU_HEIGHT = 30;
const HEADER_CTA_WIDTH: TDeviceValues = { desktop: 156, tablet: 166, mobile: 142 };
const HEADER_CTA_HEIGHT: TDeviceValues = { desktop: 44, tablet: 44, mobile: 34 };
const HEADER_TEXT_VISIBILITY: TWebsiteBuilderHideableNodeVisibility = { desktop: true, tablet: true, mobile: false };

function headerBoundaryWidth(device: TWebsiteBuilderDevice): number {
  if (device === "tablet") {
    return WEBSITE_BUILDER_DESIGN_SPACE_BOUNDARY_TABLET_WIDTH;
  }

  if (device === "mobile") {
    return WEBSITE_BUILDER_DESIGN_SPACE_BOUNDARY_MOBILE_WIDTH;
  }

  return WEBSITE_BUILDER_DESIGN_SPACE_BOUNDARY_DESKTOP_WIDTH;
}

function headerContentFrame(device: TWebsiteBuilderDevice): { left: number; right: number; width: number } {
  const inset = HEADER_CONTENT_INSET[device];
  const boundaryWidth = headerBoundaryWidth(device);

  return {
    left: inset,
    right: boundaryWidth - inset,
    width: boundaryWidth - inset * 2,
  };
}

export function websiteBuilderHeaderPresetIncludesCta(preset: TWebsiteBuilderHeaderPreset): boolean {
  return preset !== "logo-left-links-right";
}

function headerElements(header: IWebsiteBuilderHeader): TWebsiteBuilderElement[] {
  const elements: TWebsiteBuilderElement[] = [];

  function traverse(node: TWebsiteBuilderNode) {
    if (node == null) {
      return;
    }

    if (isWebsiteBuilderElement(node)) {
      elements.push(node);
      return;
    }

    if (isWebsiteBuilderParentNode(node)) {
      node.children.forEach(traverse);
    }
  }

  traverse(header);

  return elements;
}

function resetPosition(element: TWebsiteBuilderElement): void {
  DEVICES.forEach(device => {
    element.element.layout[device].position = { x: 0, y: 0 };
  });
  element.element.zIndex = 0;
}

function prepareLogo(logo: IWebsiteBuilderLogo): IWebsiteBuilderLogo {
  const next = cloneDeep(logo ?? websiteBuilderGenerateDefaultLogo());

  resetPosition(next);
  DEVICES.forEach(device => {
    next.element.layout[device].dimensions = {
      width: HEADER_LOGO_WIDTH[device],
      height: HEADER_LOGO_HEIGHT[device],
    };
  });

  return next;
}

function prepareNavigationMenu(navigationMenu: IWebsiteBuilderNavigationMenu): IWebsiteBuilderNavigationMenu {
  const next = cloneDeep(navigationMenu ?? websiteBuilderGenerateDefaultNavigationMenu());

  resetPosition(next);
  DEVICES.forEach(device => {
    next.element.layout[device].dimensions =
      device === "desktop"
        ? { width: HEADER_DESKTOP_MENU_WIDTH, height: HEADER_DESKTOP_MENU_HEIGHT }
        : { width: HEADER_COMPACT_MENU_WIDTH, height: HEADER_COMPACT_MENU_HEIGHT };
  });

  return next;
}

function createDefaultCta(): IWebsiteBuilderButton {
  const button = websiteBuilderGenerateDefaultButton();

  button.element.settings = {
    ...button.element.settings,
    label: "Book a tee time",
  };

  return button;
}

function prepareCta(button: IWebsiteBuilderButton): IWebsiteBuilderButton {
  const next = cloneDeep(button ?? createDefaultCta());

  resetPosition(next);
  next.element.visibility = HEADER_TEXT_VISIBILITY;
  DEVICES.forEach(device => {
    next.element.layout[device].dimensions = {
      width: HEADER_CTA_WIDTH[device],
      height: HEADER_CTA_HEIGHT[device],
    };
  });

  return next;
}

function placeInRowY(rowHeight: number, elementHeight: number): number {
  return Math.max(0, Math.round((rowHeight - elementHeight) / 2));
}

function placeMainRowElements(
  preset: TWebsiteBuilderHeaderPreset,
  logo: IWebsiteBuilderLogo,
  navigationMenu: IWebsiteBuilderNavigationMenu,
  cta: IWebsiteBuilderButton,
): void {
  DEVICES.forEach(device => {
    const frame = headerContentFrame(device);
    const rowHeight = HEADER_MAIN_ROW_HEIGHT[device];
    const logoWidth = logo.element.layout[device].dimensions.width;
    const logoHeight = logo.element.layout[device].dimensions.height;
    const navWidth = navigationMenu.element.layout[device].dimensions.width;
    const navHeight = navigationMenu.element.layout[device].dimensions.height;
    const ctaVisible = cta?.element.visibility[device] ?? false;
    const ctaBoxWidth = cta == null ? 0 : cta.element.layout[device].dimensions.width;
    const ctaHeight = cta == null ? 0 : cta.element.layout[device].dimensions.height;

    let logoX = frame.left;
    let navX = frame.right - navWidth;
    let ctaX = frame.right - ctaBoxWidth;

    if (device === "desktop") {
      if (preset === "logo-left-links-right-cta-right") {
        navX = (ctaVisible ? ctaX - HEADER_LAYOUT_GAP : frame.right) - navWidth;
      } else if (preset === "logo-left-links-center-cta-right") {
        navX = frame.left + Math.round((frame.width - navWidth) / 2);
      } else if (preset === "logo-center-links-left-cta-right") {
        navX = frame.left;
        logoX = frame.left + Math.round((frame.width - logoWidth) / 2);
      } else if (preset === "logo-left-links-left-cta-right") {
        navX = frame.left + logoWidth + HEADER_LAYOUT_GAP;
      }
    } else if (cta != null && ctaVisible) {
      ctaX = navX - HEADER_LAYOUT_GAP - ctaBoxWidth;
    }

    logo.element.layout[device].position = {
      x: logoX,
      y: placeInRowY(rowHeight, logoHeight),
    };
    navigationMenu.element.layout[device].position = {
      x: navX,
      y: placeInRowY(rowHeight, navHeight),
    };

    if (cta != null) {
      cta.element.layout[device].position = {
        x: ctaX,
        y: placeInRowY(rowHeight, ctaHeight),
      };
    }
  });
}

export function websiteBuilderApplyHeaderPreset(
  header: IWebsiteBuilderHeader,
  preset: TWebsiteBuilderHeaderPreset,
): IWebsiteBuilderHeader {
  const elements = headerElements(header);
  const logo = prepareLogo(elements.find(isWebsiteBuilderLogo));
  const navigationMenu = prepareNavigationMenu(elements.find(isWebsiteBuilderNavigationMenu));
  const cta = websiteBuilderHeaderPresetIncludesCta(preset) ? prepareCta(elements.find(isWebsiteBuilderButton)) : null;

  placeMainRowElements(preset, logo, navigationMenu, cta);

  const children: IWebsiteBuilderHeader["children"] = [logo, navigationMenu];

  if (cta != null) {
    children.push(cta);
  }

  return {
    ...header,
    section: {
      ...header.section,
      layout: {
        desktop: { height: HEADER_MAIN_ROW_HEIGHT.desktop },
        tablet: { height: HEADER_MAIN_ROW_HEIGHT.tablet },
        mobile: { height: HEADER_MAIN_ROW_HEIGHT.mobile },
      },
    },
    children,
  };
}
